import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Fingerprint, LogIn, LogOut, Coffee, Clock, CalendarDays, Loader2 } from 'lucide-react';
import { registerPasskey, punchWithPasskey, passkeySupported } from '../utils/webauthnHr';
import { LunchPopup } from '../components/hr/LunchPopup';
import { AbsenceSection } from '../components/hr/AbsenceSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('hw_token')}` });

const PUNCH_LABEL = { in: 'Entrata', out: 'Uscita' };

export default function TimbraturaPage() {
  const [me, setMe] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/hr/me`, { headers: authHeaders() });
      setMe(r.data);
      setError('');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Errore di caricamento');
    }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const doRegister = async () => {
    setBusy(true);
    try {
      await registerPasskey();
      toast.success('Passkey registrata! Ora puoi timbrare con la tua impronta');
      load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const doPunch = async (type) => {
    setBusy(true);
    try {
      const r = await punchWithPasskey(type);
      toast.success(r.message || 'Timbratura registrata');
      load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6" data-testid="timbratura-page">
        <p className="text-slate-300 text-center" data-testid="hr-error">{error}</p>
      </div>
    );
  }
  if (!me) {
    return <div className="min-h-screen bg-slate-900 flex items-center justify-center" data-testid="timbratura-page"><Loader2 className="h-8 w-8 text-white animate-spin" /></div>;
  }

  const next = me.next || {};
  const punches = me.today?.punches || [];
  const actionIcon = next.action === 'in' ? <LogIn className="h-10 w-10" /> :
    next.action === 'out' && next.label === 'Vai in pausa' ? <Coffee className="h-10 w-10" /> : <LogOut className="h-10 w-10" />;

  return (
    <div className="min-h-screen bg-slate-900 text-white p-6" data-testid="timbratura-page">
      <LunchPopup me={me} busy={busy} onPunch={doPunch} />
      <div className="max-w-md mx-auto space-y-6">
        <header className="pt-4 flex items-end justify-between">
          <div>
            <p className="text-slate-400 text-sm">{new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
            <h1 className="text-2xl font-bold" data-testid="hr-employee-name">Ciao, {me.employee?.name?.split(' ')[0]} 👋</h1>
          </div>
          <a href="/guida-timbratura" data-testid="hr-guide-link"
            className="text-xs text-slate-300 border border-slate-600 rounded-lg px-3 py-1.5 hover:bg-slate-800 whitespace-nowrap">
            📖 Guida
          </a>
        </header>

        {!me.has_passkey ? (
          <div className="bg-slate-800 rounded-2xl p-6 space-y-4 text-center">
            <Fingerprint className="h-14 w-14 mx-auto text-emerald-400" />
            <p className="text-slate-300 text-sm">Per timbrare devi prima registrare la <b>passkey</b> su questo telefono: userai la tua impronta o il tuo volto, così la timbratura è solo tua.</p>
            {!passkeySupported() && <p className="text-rose-400 text-xs">⚠️ Questo browser non supporta le passkey — usa Chrome/Safari aggiornato sul tuo telefono.</p>}
            <Button data-testid="hr-register-passkey-btn" onClick={doRegister} disabled={busy || !passkeySupported()}
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Registra questo telefono'}
            </Button>
          </div>
        ) : (
          <div className="bg-slate-800 rounded-2xl p-6 text-center space-y-4">
            {next.action === 'rest' || next.action === 'done' ? (
              <p className="text-lg text-slate-300" data-testid="hr-day-status">{next.label}</p>
            ) : (
              <>
                <p className="text-slate-400 text-sm">Prossima azione</p>
                <button data-testid="hr-punch-btn" disabled={busy}
                  onClick={() => doPunch(next.action)}
                  className={`w-40 h-40 mx-auto rounded-full flex flex-col items-center justify-center gap-2 font-bold text-lg transition-transform active:scale-95 ${next.action === 'in' ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-amber-600 hover:bg-amber-500'}`}>
                  {busy ? <Loader2 className="h-10 w-10 animate-spin" /> : actionIcon}
                  {next.label}
                </button>
                <p className="text-xs text-slate-500 flex items-center justify-center gap-1"><Fingerprint className="h-3 w-3" /> richiede la tua impronta/volto</p>
              </>
            )}
          </div>
        )}

        <div className="bg-slate-800 rounded-2xl p-5 space-y-3">
          <p className="text-sm font-semibold text-slate-300 flex items-center gap-2"><Clock className="h-4 w-4" /> Oggi</p>
          {punches.length === 0 ? <p className="text-slate-500 text-sm" data-testid="hr-no-punches">Nessuna timbratura</p> : (
            <div className="space-y-1" data-testid="hr-punch-list">
              {punches.map((p, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-slate-400">{PUNCH_LABEL[p.type]}{p.auto ? ' (auto)' : ''}</span>
                  <span>
                    {new Date(p.ts).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                    {p.late_minutes > 0 && <span className="text-rose-400 ml-2">+{p.late_minutes}m ritardo</span>}
                    {p.early_minutes > 0 && <span className="text-amber-400 ml-2">-{p.early_minutes}m anticipo</span>}
                  </span>
                </div>
              ))}
            </div>
          )}
          {me.today?.worked_minutes > 0 && (
            <p className="text-xs text-slate-500 pt-1 border-t border-slate-700">Ore lavorate: {Math.floor(me.today.worked_minutes / 60)}h {me.today.worked_minutes % 60}m</p>
          )}
        </div>

        <div className="bg-slate-800 rounded-2xl p-5">
          <p className="text-sm font-semibold text-slate-300 flex items-center gap-2 mb-3"><CalendarDays className="h-4 w-4" /> I tuoi saldi</p>
          <div className="grid grid-cols-2 gap-3 text-center">
            <div className="bg-slate-700/50 rounded-xl p-3">
              <p className="text-2xl font-bold text-emerald-400" data-testid="hr-ferie-balance">{Number(me.employee?.ferie_balance_days || 0).toFixed(2)}</p>
              <p className="text-xs text-slate-400">giorni di ferie</p>
            </div>
            <div className="bg-slate-700/50 rounded-xl p-3">
              <p className="text-2xl font-bold text-sky-400" data-testid="hr-rol-balance">{Number(me.employee?.rol_balance_hours || 0).toFixed(2)}</p>
              <p className="text-xs text-slate-400">ore di permesso</p>
            </div>
          </div>
        </div>

        <AbsenceSection onChanged={load} />
      </div>
    </div>
  );
}
